import { motion } from "framer-motion";
import { CalendarCheck, ShieldCheck } from "lucide-react";
import { PAYMENT_URL, useLang } from "@/i18n";

export default function TuitionBanner() {
  const { t } = useLang();
  const c = t.tuition;

  return (
    <section id="cuota" className="px-5 py-24 sm:px-8 sm:py-32">
      <div className="mx-auto max-w-7xl">
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          className="mb-4 font-mono text-xs font-bold uppercase tracking-[0.3em] text-accent"
        >
          {c.label}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="relative overflow-hidden rounded-2xl border-2 border-accent bg-gradient-to-r from-[#180010] via-[#28001C] to-panel p-7 shadow-[0_0_50px_rgba(255,0,122,0.22)] sm:p-12"
        >
          <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-accent/25 blur-[100px]" />

          <div className="relative grid grid-cols-1 items-center gap-10 lg:grid-cols-[auto_1fr_auto]">
            <div className="flex items-baseline gap-2">
              <span className="font-display text-6xl font-black text-white sm:text-8xl">{c.price}</span>
              <span className="font-mono text-lg font-bold text-zinc-400">{c.per}</span>
            </div>

            <div>
              <h2 className="font-display text-xl font-extrabold uppercase tracking-tight sm:text-3xl">{c.title}</h2>
              <p className="mt-3 max-w-xl text-sm leading-relaxed text-zinc-300 sm:text-base">{c.desc}</p>
              <div className="mt-5 inline-flex flex-wrap items-center gap-2 rounded-full border border-accent/50 bg-accent/10 px-4 py-2">
                <CalendarCheck size={16} className="text-accent" />
                <span className="font-mono text-xs font-bold uppercase tracking-[0.15em] text-white">{c.window}</span>
                <span className="font-mono text-xs text-zinc-400">({c.windowNote})</span>
              </div>
              <div className="mt-6 grid max-w-xl grid-cols-1 gap-2.5 sm:grid-cols-3">
                {c.types.map((ty) => (
                  <div key={ty.t} className="rounded-lg border border-white/10 bg-black/25 p-3.5" data-testid="tuition-type-card">
                    <p className="font-display text-xs font-extrabold uppercase tracking-wide text-white">{ty.t}</p>
                    <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.1em] text-zinc-400">{ty.d}</p>
                  </div>
                ))}
              </div>
              <p className="mt-5 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-accent">{c.includesTitle}</p>
              <ul className="mt-2 flex flex-wrap gap-x-5 gap-y-1.5">
                {c.includes.map((inc) => (
                  <li key={inc} className="flex items-center gap-1.5 text-xs text-zinc-300">
                    <CalendarCheck size={12} className="text-accent" /> {inc}
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex flex-col items-stretch gap-3">
              <a
                data-testid="pay-tuition-banner-button"
                href={PAYMENT_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-accent px-8 py-4 font-mono text-sm font-bold uppercase tracking-[0.15em] text-white shadow-[0_0_28px_rgba(255,0,122,0.45)] transition-all duration-300 hover:scale-[1.03] hover:bg-accent-hover hover:shadow-[0_0_44px_rgba(255,0,122,0.65)]"
              >
                <ShieldCheck size={18} />
                {c.button}
              </a>
              <span className="text-center font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">
                {c.secure}
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
